import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    afterNextRender,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { EngagementSystemAlert } from "../engagement-metrics/engagement.service";
import { S25CardCarouselItemDirective } from "../../../standalone/card-carousel/s25.card.carousel.item.directive";

@Component({
    selector: "connect25-system-alert-card",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    hostDirectives: [S25CardCarouselItemDirective],
    host: {
        "[attr.data-alert-id]": "alert().engagementSystemAlertId",
        class: "connect25-sys-alert-card",
        "[class.connect25-sys-alert-card--expand-body]": "expandBody()",
        "[style.--connect25-alert-clamp-lines]": "clampLines()",
    },
    template: `
        <div
            class="connect25-sys-alert-card__body"
            [class.is-clamped]="showBodyClamp()"
            [attr.data-alert-id]="alert().engagementSystemAlertId"
            [innerHTML]="safeHtml()"
            [attr.tabindex]="bodyTabIndex()"
        ></div>
        @if (showReadMore() || showAcknowledge()) {
            <div class="connect25-sys-alert-card__actions">
                @if (showReadMore()) {
                    <button
                        type="button"
                        class="connect25-sys-alert-card__readmore"
                        (click)="onReadMore()"
                        [attr.aria-label]="readMoreLabel()"
                    >
                        {{ isExpanded() ? "Read less" : "Read more" }}
                    </button>
                }
                @if (showAcknowledge()) {
                    <button
                        type="button"
                        class="aw-button aw-button--outline connect25-sys-alert-card__ack"
                        (click)="onDismiss()"
                        [attr.aria-label]="acknowledgeLabel()"
                    >
                        Acknowledge
                    </button>
                }
            </div>
        }
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            justify-content: space-between;
            padding: 0.75rem 1rem;
            background: var(--color--surface-bg);
            color: var(--color--text);
            border: 1px solid var(--color--border);
            border-radius: 6px;
        }

        .connect25-sys-alert-card__body {
            min-width: 0;
        }

        @media (min-width: 931px) {
            .connect25-sys-alert-card__body.is-clamped {
                display: -webkit-box;
                -webkit-line-clamp: var(--connect25-alert-clamp-lines, 4);
                line-clamp: var(--connect25-alert-clamp-lines, 4);
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            .connect25-sys-alert-card__body:not(.is-clamped) {
                max-height: 15rem;
                overflow-y: auto;
                overscroll-behavior: contain;
                scrollbar-width: thin;
            }
        }

        @media (max-width: 930px) {
            :host(.connect25-sys-alert-card--expand-body) .connect25-sys-alert-card__body {
                flex: 1;
                min-height: 0;
                overflow-y: auto;
                overscroll-behavior: contain;
                scrollbar-width: thin;
            }

            :host(:not(.connect25-sys-alert-card--expand-body)) .connect25-sys-alert-card__body {
                max-height: 55vh;
                overflow-y: auto;
                overscroll-behavior: contain;
                scrollbar-width: thin;
            }

            .connect25-sys-alert-card__readmore {
                display: none;
            }
        }

        .connect25-sys-alert-card__actions {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }

        .connect25-sys-alert-card__readmore {
            background: transparent;
            border: 0;
            padding: 0.25rem;
            min-height: 1.5rem;
            color: var(--color--link-text);
            text-decoration: underline;
            cursor: pointer;
        }

        .connect25-sys-alert-card__readmore:focus-visible {
            outline: none;
            box-shadow: var(--shadow--inner-focus);
        }

        .connect25-sys-alert-card__ack {
            margin-inline-start: auto;
        }
    `,
})
export class Connect25SystemAlertCardComponent {
    private readonly sanitizer = inject(DomSanitizer);
    private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

    readonly alert = input.required<EngagementSystemAlert>();
    readonly index = input.required<number>();
    readonly total = input.required<number>();
    /** Whether to show the Acknowledge button. Pass false for read-only surfaces. */
    readonly showAcknowledge = input(true);
    /** Number of desktop clamp lines; also set via --connect25-alert-clamp-lines. */
    readonly clampLines = input(4);
    /**
     * When true, the card body grows to fill remaining height on mobile (for the banner surface
     * where the strip fills the viewport). When false, the body is capped at 55vh.
     */
    readonly expandBody = input(false);

    readonly dismiss = output<number>();

    private readonly mobileQuery = window.matchMedia("(max-width: 930px)");
    readonly isMobile = signal(this.mobileQuery.matches);

    private readonly isClamped = signal(false);
    readonly isExpanded = signal(false);

    readonly safeHtml = computed(() => this.sanitizer.bypassSecurityTrustHtml(this.alert().bodyHtml ?? ""));

    readonly showBodyClamp = computed(() => !this.isMobile() && !this.isExpanded());
    readonly showReadMore = computed(() => !this.isMobile() && (this.isClamped() || this.isExpanded()));
    readonly bodyTabIndex = computed(() => (this.isMobile() || this.isExpanded() ? 0 : null));

    private readonly position = computed(() => `notice ${this.index() + 1} of ${this.total()}`);
    readonly acknowledgeLabel = computed(() => `Acknowledge ${this.position()}`);
    readonly readMoreLabel = computed(() =>
        this.isExpanded() ? `Show less of ${this.position()}` : `Read more about ${this.position()}`,
    );

    constructor() {
        effect((onCleanup) => {
            const onMobileChange = (event: MediaQueryListEvent) => this.isMobile.set(event.matches);
            this.mobileQuery.addEventListener("change", onMobileChange);
            onCleanup(() => this.mobileQuery.removeEventListener("change", onMobileChange));
        });

        afterNextRender(() => this.measureClamp());

        effect(() => {
            this.alert();
            this.isExpanded();
            this.isMobile();
            queueMicrotask(() => this.measureClamp());
        });
    }

    onReadMore(): void {
        this.isExpanded.update((expanded) => !expanded);
    }

    onDismiss(): void {
        const alertId = this.alert().engagementSystemAlertId;
        if (alertId == null) return;
        this.dismiss.emit(alertId);
    }

    private measureClamp(): void {
        // On mobile the card body scrolls natively — clamp is off, so never report it as read-more eligible.
        if (this.isMobile()) {
            if (this.isClamped()) this.isClamped.set(false);
            this.syncFocusables();
            return;
        }

        const bodyEl = this.hostRef.nativeElement.querySelector<HTMLElement>(".connect25-sys-alert-card__body");
        if (!bodyEl) return;

        if (this.isExpanded()) {
            // While expanded the clamp CSS is off, so overflow reads zero. Preserve the prior clamped status
            // rather than dropping the Read-more affordance while the user is reading the expanded text.
            this.syncFocusables();
            return;
        }

        const nowClamped = bodyEl.scrollHeight - bodyEl.clientHeight > 1;
        if (nowClamped !== this.isClamped()) this.isClamped.set(nowClamped);
        this.syncFocusables();
    }

    /**
     * When the body is collapsed (clamped), remove focusable elements inside it from the tab order
     * so keyboard users cannot navigate to invisible links. Restores tabindex on expand.
     *
     * This is the constraint most likely to be dropped on refactor (phase-1 spec §4) — migrated
     * intentionally with a spec that will fail if it regresses.
     */
    private syncFocusables(): void {
        const bodyEl = this.hostRef.nativeElement.querySelector<HTMLElement>(".connect25-sys-alert-card__body");
        if (!bodyEl) return;

        const collapsed = this.isClamped() && !this.isExpanded() && !this.isMobile();
        bodyEl
            .querySelectorAll<HTMLElement>("a[href], button, input, select, textarea, [tabindex]")
            .forEach((focusable) => {
                if (collapsed) {
                    if (!focusable.hasAttribute("data-clamp-prev-tabindex")) {
                        focusable.setAttribute("data-clamp-prev-tabindex", focusable.getAttribute("tabindex") ?? "");
                    }
                    focusable.setAttribute("tabindex", "-1");
                } else if (focusable.hasAttribute("data-clamp-prev-tabindex")) {
                    const previous = focusable.getAttribute("data-clamp-prev-tabindex");
                    if (previous) {
                        focusable.setAttribute("tabindex", previous);
                    } else {
                        focusable.removeAttribute("tabindex");
                    }
                    focusable.removeAttribute("data-clamp-prev-tabindex");
                }
            });
    }
}
